import React, { useEffect } from "react";
import { CheckCircle, X } from "lucide-react";

interface SuccessModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

export function SuccessModal({
  isOpen,
  title,
  message,
  onClose,
  autoClose = true,
  autoCloseDelay = 3000
}: SuccessModalProps) {
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "var(--card)",
          borderRadius: "20px",
          padding: "40px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.3), 0 8px 16px rgba(0, 0, 0, 0.2)",
          position: "relative",
          animation: "modalBounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          textAlign: "center",
          border: "1px solid var(--border)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "var(--muted)",
            border: "none",
            cursor: "pointer",
            color: "var(--muted-foreground)",
            padding: "8px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            width: "32px",
            height: "32px"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--accent)";
            e.currentTarget.style.color = "white";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--muted)";
            e.currentTarget.style.color = "var(--muted-foreground)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <X style={{ width: "18px", height: "18px" }} />
        </button>

        <div style={{ marginBottom: "28px" }}>
          <div
            style={{
              width: "88px",
              height: "88px",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              animation: "checkmarkPulse 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.4), 0 0 0 0 rgba(16, 185, 129, 0.4)",
              position: "relative"
            }}
          >
            <div style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)",
            }} />
            <CheckCircle style={{ width: "44px", height: "44px", color: "white", position: "relative", zIndex: 1 }} />
          </div>
        </div>

        <h3
          style={{
            fontSize: "26px",
            fontWeight: "700",
            color: "var(--card-foreground)",
            marginBottom: "12px",
            margin: "0 0 12px 0",
            letterSpacing: "-0.5px"
          }}
        >
          {title}
        </h3>

        <p
          style={{
            color: "var(--muted-foreground)",
            fontSize: "16px",
            lineHeight: "1.6",
            margin: "0"
          }}
        >
          {message}
        </p>

        <div
          style={{
            marginTop: "32px",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "12px 32px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: "120px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.4)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(16, 185, 129, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.4)";
            }}
          >
            OK
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes modalBounceIn {
            0% {
              opacity: 0;
              transform: scale(0.7) translateY(-30px);
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          @keyframes checkmarkPulse {
            0% {
              transform: scale(0);
              box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4), 0 0 0 0 rgba(16, 185, 129, 0.4);
            }
            50% {
              transform: scale(1.1);
              box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4), 0 0 0 20px rgba(16, 185, 129, 0);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4), 0 0 0 0 rgba(16, 185, 129, 0);
            }
          }
        `}
      </style>
    </div>
  );
}
